import { ClientDto, TeamDto } from '@act/shared/data-transfer-objects';
import { UserRole } from '@act/shared/definitions';
import { ControlledAutocomplete } from '@act/storybook';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Drawer,
  FormControl,
  FormLabel,
  IconButton,
  Typography,
} from '@mui/material';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { Control } from 'react-hook-form';

interface UserAdminFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  onReset: () => void;
  control: Control<{
    roles: string[];
    teamIds: string[];
    clientIds: string[];
    hasClient: string | null;
  }>;
  teams: TeamDto[];
  clients: ClientDto[];
}

const roleOptions = [
  { label: 'Admin', value: UserRole.ADMIN },
  {
    label: 'Community Outreach Specialist',
    value: UserRole.COMMUNITY_OUTREACH_SPECIALIST,
  },
  { label: 'Guide', value: UserRole.GUIDE },
  { label: 'Guide Supervisor', value: UserRole.GUIDE_SUPERVISOR },
  { label: 'Provider', value: UserRole.PROVIDER },
  { label: 'Super Admin', value: UserRole.SUPER_ADMIN },
].sort((a, b) => a.label.localeCompare(b.label));

const hasClientOptions = [
  { label: 'Has Managed Clients', value: 'true' },
  { label: 'No Managed Clients', value: 'false' },
].sort((a, b) => a.label.localeCompare(b.label));

export const UserAdminFilterDrawer = ({
  open,
  onClose,
  onApply,
  onReset,
  control,
  teams,
  clients,
}: UserAdminFilterDrawerProps) => {
  const { generateTrackingProps } = useTracking();

  const teamOptions =
    teams
      ?.map(team => ({
        label: team.name,
        value: String(team.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  const clientOptions =
    clients
      ?.map(client => ({
        label: client.name,
        value: String(client.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)) || [];

  return (
    <TrackingProvider contextName="user-admin-filter-drawer">
      <Drawer anchor="right" open={open} onClose={onClose}>
        <Box
          sx={{
            width: { xs: '100vw', sm: 400 },
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant="h6">Filters</Typography>
            <IconButton
              onClick={onClose}
              {...generateTrackingProps('close-button')}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
              }}
            >
              {/* Role Filter */}
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Filter by Role</FormLabel>
                <ControlledAutocomplete
                  name="roles"
                  control={control}
                  options={roleOptions}
                  getOptionLabel={(option: { label: string; value: string }) =>
                    option.label
                  }
                  label="Select roles"
                  multiple={true}
                  placeholder="Select roles"
                  trackingName="roles-filter-autocomplete"
                />
              </FormControl>

              {/* Team Filter */}
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Filter by Team</FormLabel>
                <ControlledAutocomplete
                  name="teamIds"
                  control={control}
                  options={teamOptions}
                  getOptionLabel={(option: { label: string; value: string }) =>
                    option.label
                  }
                  label="Select teams"
                  multiple={true}
                  placeholder="Select teams"
                  trackingName="teams-filter-autocomplete"
                />
              </FormControl>

              {/* Client Filter */}
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Filter by Managed Client</FormLabel>
                <ControlledAutocomplete
                  name="clientIds"
                  control={control}
                  options={clientOptions}
                  getOptionLabel={(option: { label: string; value: string }) =>
                    option.label
                  }
                  label="Select clients"
                  multiple={true}
                  placeholder="Select clients"
                  trackingName="clients-filter-autocomplete"
                />
              </FormControl>

              {/* Has Client Filter */}
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1 }}>Client Management Status</FormLabel>
                <ControlledAutocomplete
                  name="hasClient"
                  control={control}
                  options={hasClientOptions}
                  getOptionLabel={(option: { label: string; value: string }) =>
                    option?.label || ''
                  }
                  label="Select status"
                  multiple={false}
                  placeholder="Select status"
                  trackingName="has-client-filter-autocomplete"
                />
              </FormControl>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                p: 2,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                gap: 2,
                justifyContent: 'flex-end',
              }}
            >
              <Button variant="outlined" onClick={onReset}>
                Reset
              </Button>
              <Button variant="contained" onClick={onApply}>
                Apply
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>
    </TrackingProvider>
  );
};
